import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import QRCode from 'qrcode';
import {
  APP_STORE_BADGE_IMG,
  APP_STORE_URL,
  PLAY_STORE_BADGE_IMG,
  PLAY_STORE_URL,
} from '../constants/appStore';

const IMG_STADIUM =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuDZJcljSdjVjLemY6TtW-7gsT1LdbbvMAlJ_D1no8H2RFHaGIb76tIodpmhz8DVaQ0L88CMIzL7cy8vQS85ditKcJMiQmMvhG9PlmS-KMNys_0bV_C2SQVStRGcOJx_TJ9rTSZfhQPeirLhYzDzVvP_TEHsddlrF7H9-WQMsRSAdymg8hzXGYd8M9rr-rYeCBVd2IRCWzWR_EgYnMLnUh6ZRtCtlVQ9uuY7TO4a3b0K_excyIjaW3eh_DHM3_SbFcy3gOWQRu_PFA';
const IMG_PHONE =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuA0C9s7vU27e_dCzhVoV6aJwwGgJGhkzo6BnFTaxJ9Ysv07XLlcoJP_SH4LSoSzf3nESg6pGVp3w37gONKmWAKyIzp6Mqgsf_3Pv4Cspk1h9uEYLyC4w-Ct3e5_cA4K7JiTDTwCkkjiivrab5TSvpsNSeWW_0OBUxbaI7057ZPAByZrJOHVNik1Q_kUMUReYkAEgL-zjy0xyW753phy8YK549Rux8WspNRZ7OtDAghfvZHzlmgxLAXqle0jiiq3q7h3moCmhflynQ';

const SESSION_KEY = 'meeteor_widget_app_intro_dismissed';

export function isWidgetAppIntroDismissedInSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissWidgetAppIntroInSession(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    /* */
  }
}

type Variant = 'table' | 'nonTable';

interface WidgetAppIntroProps {
  variant: Variant;
  eventName: string;
  shareUrl: string;
  primaryColor: string;
  onContinue: () => void;
}

export const WidgetAppIntro: FunctionalComponent<WidgetAppIntroProps> = ({
  variant,
  eventName,
  shareUrl,
  primaryColor,
  onContinue,
}) => {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!shareUrl) return;
    let cancelled = false;
    QRCode.toDataURL(shareUrl, {
      width: 280,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#0f0f0f', light: '#ffffff' },
    })
      .then((u) => {
        if (!cancelled) setQrDataUrl(u);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [shareUrl]);

  const cont = () => {
    dismissWidgetAppIntroInSession();
    onContinue();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      dismissWidgetAppIntroInSession();
      onContinue();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onContinue]);

  const isTable = variant === 'table';

  return (
    <div
      class={['mw-app-intro', !isTable ? 'mw-app-intro--compact' : ''].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mw-app-intro-title"
    >
      <button type="button" class="mw-app-intro__backdrop" aria-label="Close dialog" onClick={cont} />
      <div
        class={['mw-app-intro__panel', !isTable ? 'mw-app-intro__panel--compact' : ''].filter(Boolean).join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" class="mw-app-intro__close" aria-label="Close" onClick={cont}>
          ×
        </button>
        <div class="mw-app-intro__scroll">
        <header class="mw-app-intro__header">
          <a class="mw-app-intro__brand" href="https://meeteor.me" target="_blank" rel="noopener noreferrer">
            <img
              class="mw-app-intro__brand-full"
              src="/logo-light-mode.png"
              alt="Meeteor"
              width={148}
              height={36}
            />
          </a>
          <button type="button" class="mw-app-intro__header-cta" style={{ background: primaryColor }} onClick={cont}>
            Get app
          </button>
        </header>

        <main>
          {isTable ? (
            <>
              <div class="mw-app-intro__hero mw-app-intro__hero--split">
                <div>
                  <div class="mw-app-intro__pill" aria-hidden="true">
                    ⭐ <span>NOW AVAILABLE GLOBALLY</span>
                  </div>
                  <h1 class="mw-app-intro__h1" id="mw-app-intro-title">
                    Want to see your seat map and select multiple visually?
                  </h1>
                  {eventName ? (
                    <p class="mw-app-intro__event-name">{eventName}</p>
                  ) : null}
                  <p class="mw-app-intro__lead">
                    Download the Meeteor app and scan this QR code on your phone camera to buy using the app. Experience
                    high-fidelity interactive mapping at your fingertips.
                  </p>
                  <div class="mw-app-intro__badges">
                    <a
                      class="mw-app-intro__store-badge"
                      href={APP_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Download on the App Store"
                    >
                      <img src={APP_STORE_BADGE_IMG} alt="Download on the App Store" width={140} height={48} />
                    </a>
                    <a
                      class="mw-app-intro__store-badge"
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Get it on Google Play"
                    >
                      <img src={PLAY_STORE_BADGE_IMG} alt="Get it on Google Play" width={160} height={48} />
                    </a>
                  </div>
                </div>

                <div class="mw-app-intro__qr-wrap">
                  <div class="mw-app-intro__qr-card">
                    <div class="mw-app-intro__qr-fab" aria-hidden="true">
                      QR
                    </div>
                    <div class="mw-app-intro__qr-box">
                      {qrDataUrl ? (
                        <img src={qrDataUrl} alt="" width={260} height={260} />
                      ) : (
                        <span class="mw-app-intro__qr-placeholder">Generating code…</span>
                      )}
                    </div>
                    <p class="mw-app-intro__qr-hint">Scan to sync instantly</p>
                    {shareUrl && <p class="mw-app-intro__url">{shareUrl}</p>}
                  </div>
                </div>
              </div>

              <section class="mw-app-intro__bento" aria-label="App features">
                <div class="mw-app-intro__bento-hero mw-app-intro__bento-wide">
                  <img src={IMG_STADIUM} alt="" loading="lazy" />
                  <div class="mw-app-intro__bento-hero-txt">
                    <h3>Immersive visual navigation</h3>
                    <p>Fly through the venue and see the exact perspective from every seat before you purchase.</p>
                  </div>
                </div>

                <div class="mw-app-intro__bento-tile mw-app-intro__bento-tile--primary">
                  <div class="mw-app-intro__bento-icon" aria-hidden="true">
                    ▤
                  </div>
                  <div>
                    <h3>Bulk select</h3>
                    <p>Group booking made easy — select clusters of seats with a single gesture in the app.</p>
                  </div>
                </div>

                <div class="mw-app-intro__bento-tile mw-app-intro__bento-tile--soft">
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span class="mw-app-intro__pill" style={{ margin: 0, fontSize: '0.65rem' }}>
                        TICKET HUB
                      </span>
                      <span style={{ color: primaryColor }} aria-hidden="true">
                        ✓
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: '0.75rem',
                        background: '#fff',
                        borderRadius: '1rem',
                        padding: '1rem',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                      }}
                    >
                      <div style={{ height: 8, width: '65%', background: '#f1f5f9', borderRadius: 999, marginBottom: 8 }} />
                      <div style={{ height: 8, width: '45%', background: '#f8fafc', borderRadius: 999 }} />
                    </div>
                  </div>
                  <div style={{ marginTop: '1rem' }}>
                    <h3>Live mobile sync</h3>
                    <p>Your selected seats and preferences sync across devices in real time.</p>
                  </div>
                </div>

                <div class="mw-app-intro__bento-wide2" style={{ gridColumn: '1 / -1' }}>
                  <div class="mw-app-intro__feat-list">
                    <div class="mw-app-intro__feat">
                      <div class="mw-app-intro__feat-ico" aria-hidden="true">
                        $
                      </div>
                      <div>
                        <h4>One-tap checkout</h4>
                        <p>Apple Pay and Google Pay for fast, secure purchases.</p>
                      </div>
                    </div>
                    <div class="mw-app-intro__feat">
                      <div class="mw-app-intro__feat-ico" aria-hidden="true">
                        🔔
                      </div>
                      <div>
                        <h4>Inventory alerts</h4>
                        <p>Get notified when better seats open up.</p>
                      </div>
                    </div>
                  </div>
                  <div class="mw-app-intro__bento-mock">
                    <img src={IMG_PHONE} alt="" loading="lazy" />
                  </div>
                </div>
              </section>
            </>
          ) : (
            <div class="mw-app-intro__non-table">
              <div class="mw-app-intro__hero" style={{ paddingTop: '0.5rem' }}>
                <div class="mw-app-intro__pill" aria-hidden="true">
                  ⭐ <span>MEETEOR</span>
                </div>
                <h1 class="mw-app-intro__h1" id="mw-app-intro-title">
                  Save up to 20% on this reservation in the app
                </h1>
                {eventName ? <p class="mw-app-intro__event-name">{eventName}</p> : null}
                <p class="mw-app-intro__lead">
                  You could save up to 20% on this reservation by using the Meeteor app. Download Meeteor, then scan the QR
                  code to open this event in the app and get the best price.
                </p>
                <div class="mw-app-intro__badges">
                  <a
                    class="mw-app-intro__store-badge"
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download on the App Store"
                  >
                    <img src={APP_STORE_BADGE_IMG} alt="Download on the App Store" width={140} height={48} />
                  </a>
                  <a
                    class="mw-app-intro__store-badge"
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get it on Google Play"
                  >
                    <img src={PLAY_STORE_BADGE_IMG} alt="Get it on Google Play" width={160} height={48} />
                  </a>
                </div>

                <div class="mw-app-intro__qr-wrap" style={{ marginTop: '2rem' }}>
                  <div class="mw-app-intro__qr-card">
                    <div class="mw-app-intro__qr-fab" aria-hidden="true">
                      QR
                    </div>
                    <div class="mw-app-intro__qr-box">
                      {qrDataUrl ? (
                        <img src={qrDataUrl} alt="" width={260} height={260} />
                      ) : (
                        <span class="mw-app-intro__qr-placeholder">Generating code…</span>
                      )}
                    </div>
                    <p class="mw-app-intro__qr-hint">Scan to open in Meeteor</p>
                    {shareUrl && <p class="mw-app-intro__url">{shareUrl}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

        <footer class="mw-app-intro__footer">
          <p class="mw-app-intro__footer-copyright">© {new Date().getFullYear()} Meeteor. All rights reserved.</p>
          <div class="mw-app-intro__footer-links">
            <a href="https://meeteor.me/privacy-policy" target="_blank" rel="noopener noreferrer">
              Privacy
            </a>
            <a href="https://meeteor.me/terms-of-use" target="_blank" rel="noopener noreferrer">
              Terms
            </a>
            <a href="https://meeteor.me/help-center" target="_blank" rel="noopener noreferrer">
              Support
            </a>
          </div>
        </footer>
        </main>
        </div>
        <div class="mw-app-intro__footer-actions">
          <button type="button" class="mw-app-intro__continue" style={{ background: primaryColor }} onClick={cont}>
            Continue in browser
          </button>
        </div>
      </div>
    </div>
  );
};
